'use client';

import { useRouter } from 'next/navigation';

import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { getUserProfileLink } from '@shared/helpers/user';
import { useApiQuery } from '@shared/hooks/useApiQuery';

import { getApplication, getUserActivity } from '@api/applications/general';

import { ReviewCard } from '@ui/uikit/components/common/cards/ReviewCard';
import { CardsCarousel } from '@ui/uikit/components/common/CardsCarousel';
import { type FeedbackButtonGroupSelection } from '@ui/uikit/components/common/FeedbackButtonGroup';

import { ReviewReportingController } from '../../ReviewReportingController';
import { ReviewSharingControl, ReviewSharingController } from '../../ReviewSharingControl';

export type FeedbackType = 'Review' | 'Suggestion';

export const feedbackKeyMap = {
  Suggestion: 'suggestions',
  Review: 'reviews',
} as const;

export interface FeedbackItem {
  id: number;
  text: string;
  createTime: string | number | Date;
  author: {
    name: string | null;
    icon: string;
    karma?: number;
    magicId?: string;
  };
  appId?: number;
  appName?: string;
  positive: number;
  negative: number;
  funny: number;
  rating?: number;
}

export interface ApplicationUserFeedbackCarouselProps {
  applicationSlug: string;
  feedbackItems: FeedbackItem[];
  isPending: boolean;
  feedbackType: FeedbackType;
  onHelpfulnessToggle: (selection: FeedbackButtonGroupSelection, id: number) => void;
  onFunnyReactionToggle: (state: 'on' | 'off', id: number) => void;
}

const ApplicationUserFeedbackCarousel = ({
  applicationSlug,
  feedbackItems,
  isPending,
  feedbackType,
  onHelpfulnessToggle,
  onFunnyReactionToggle,
}: ApplicationUserFeedbackCarouselProps) => {
  const { data: application } = useQuery({
    queryKey: ['application', applicationSlug],
    queryFn: () => getApplication(applicationSlug),
  });

  const appName = application?.payload?.app_name || '';
  const logoUrl = application?.payload?.icon || '';

  const { isAuth } = useAuth();
  const router = useRouter();

  const { data: userActivity } = useApiQuery(getUserActivity, [applicationSlug], {
    enabled: isAuth,
  });

  const onOpenReview = useCallback(
    (id: number) =>
      router.push(`/projects/${applicationSlug}/${feedbackType.toLowerCase()}s/${id}`),
    [router, applicationSlug, feedbackType],
  );

  return (
    <CardsCarousel
      items={feedbackItems}
      isPending={isPending || !application}
      breakout
      breakpoints={{
        SMALL: {
          contentPadding: 16,
        },
        MEDIUM: {
          contentPadding: 16,
        },
        LARGE: {
          contentPadding: 16,
        },
      }}
      render={(feedbackItem) => {
        const { id, text, createTime, author, positive, negative, funny, rating } = feedbackItem;
        const reactions = { positive, negative, funny };
        const active = userActivity?.payload[feedbackKeyMap[feedbackType]]?.[id.toString()]?.vote;

        return (
          <ReviewCard
            key={id}
            id={id}
            appName={appName}
            logoUrl={logoUrl}
            excerpt={text}
            date={new Date(createTime)}
            feedbackType={feedbackType}
            author={{
              ...author,
              profileLink: getUserProfileLink(author?.magicId),
            }}
            reactions={reactions}
            rating={rating}
            activeReactions={{
              positive: active?.vote === 1,
              negative: active?.vote === -1,
              funny: !!active?.funny,
            }}
            onHelpfulnessToggle={(selection) => onHelpfulnessToggle(selection, id)}
            onFunnyReactionToggle={(state) => onFunnyReactionToggle(state, id)}
            onOpenReview={onOpenReview}
            isCursorPointer
            sharingControl={
              <ReviewSharingController
                appName={appName}
                applicationSlug={applicationSlug}
                feedbackType={feedbackType}
                reviewId={id}
              >
                <ReviewSharingControl
                  author={author.name ?? undefined}
                  applicationName={appName}
                  feedbackType={feedbackType}
                />
              </ReviewSharingController>
            }
            reportingControl={
              <ReviewReportingController
                applicationSlug={applicationSlug}
                feedbackItem={feedbackItem}
                feedbackType={feedbackType}
                applicationName={appName}
              />
            }
          />
        );
      }}
    />
  );
};

export { ApplicationUserFeedbackCarousel };
